import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Users, Store, CalendarDays, BookOpen, Search, X, Mail, Trash2, KeyRound } from "lucide-react";
import { StepBadge } from "./common";

const USER_TABS = [
  { key: "parent", label: "Parents", icon: Users },
  { key: "vendor", label: "Vendors", icon: Store },
  { key: "event_creator", label: "Event Creators", icon: CalendarDays },
  { key: "marketplace_user", label: "Marketplace", icon: BookOpen },
];

// Users tab — role sub-tabs + search + a table of all users with their
// subscription controls and quick "Email" / "Resend email" / "Delete" actions.
// The role sub-tab + search string are controlled by the parent so navigating
// away and back preserves the admin's filter.
export default function UsersTab({
  users,
  userTab,
  setUserTab,
  userSearch,
  setUserSearch,
  openUserDetail,
  setSubscription,
  openEmailDialog,
  resendStepEmail,
  deleteUser,
}) {
  const active = USER_TABS.find((t) => t.key === userTab) || USER_TABS[0];
  const q = userSearch.trim().toLowerCase();
  const [bundleOnly, setBundleOnly] = useState(false);
  const [freeOnly, setFreeOnly] = useState(false);
  const [promoFilter, setPromoFilter] = useState("all");
  const [resetBusy, setResetBusy] = useState(null);
  const sendReset = async (u) => {
    if (!window.confirm(`Send a password reset link to ${u.email}?`)) return;
    setResetBusy(u.user_id);
    try {
      const r = await api.post(`/admin/users/${u.user_id}/send-password-reset`);
      toast.success(r.data?.message || "Password reset link sent");
    } catch (e) {
      toast.error(errMsg(e, "Could not send reset link"));
    } finally {
      setResetBusy(null);
    }
  };
  const promoCodes = [...new Set(users.map((u) => u.signup_promo_code).filter(Boolean))].sort();
  const promoOk = (u) => promoFilter === "all" || (promoFilter === "any" ? !!u.signup_promo_code : promoFilter === "none" ? !u.signup_promo_code : u.signup_promo_code === promoFilter);
  const matches = (u) => (!bundleOnly || u.bundle_member) && (!freeOnly || u.free_access) && promoOk(u) && (!q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  const group = users.filter((u) => u.role === active.key && matches(u));

  return (
    <div className="space-y-4" data-testid="users-tab">
      <div className="flex gap-2 flex-wrap" data-testid="users-role-tabs">
        {USER_TABS.map(({ key, label, icon: Icon }) => {
          const count = users.filter((u) => u.role === key && matches(u)).length;
          const isActive = userTab === key;
          return (
            <button
              key={key}
              onClick={() => setUserTab(key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors border ${isActive ? "bg-sage text-white border-sage" : "bg-white/70 text-forest/70 border-sage/20 hover:bg-sage-light hover:text-sage-dark"}`}
              data-testid={`users-role-tab-${key}`}
            >
              <Icon className="w-4 h-4" />
              {label}
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/25 text-white" : "bg-softstone/70 text-forest/60"}`} data-testid={`users-count-${key}`}>{count}</span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3">
      <div className="relative max-w-md flex-1 min-w-[16rem]" data-testid="users-search-wrapper">
        <Search className="w-4 h-4 text-forest/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="pl-9 pr-9 rounded-full bg-white/80 border-sage/30"
          data-testid="users-search-input"
        />
        {userSearch && (
          <button
            onClick={() => setUserSearch("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-softstone/70 text-forest/50"
            aria-label="Clear search"
            data-testid="users-search-clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
        <button
          type="button"
          onClick={() => setBundleOnly((v) => !v)}
          aria-pressed={bundleOnly}
          className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${bundleOnly ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-white/70 text-forest/70 border-sage/20 hover:bg-amber-50"}`}
          data-testid="users-bundle-filter-btn"
        >
          Bundle Deal only
        </button>
        <button
          type="button"
          onClick={() => setFreeOnly((v) => !v)}
          aria-pressed={freeOnly}
          className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${freeOnly ? "bg-sage-light text-sage-dark border-sage/40" : "bg-white/70 text-forest/70 border-sage/20 hover:bg-sage-light/50"}`}
          data-testid="users-free-filter-btn"
        >
          Free access only
        </button>
        <select
          value={promoFilter}
          onChange={(e) => setPromoFilter(e.target.value)}
          className="px-4 py-2 rounded-full text-sm font-bold border bg-white/70 text-forest/70 border-sage/20 focus:outline-none focus:ring-2 focus:ring-sage/40"
          data-testid="users-promo-filter-select"
        >
          <option value="all">All promo codes</option>
          <option value="any">Used any promo code</option>
          <option value="none">No promo code</option>
          {promoCodes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="bg-white/80 border border-sage/20 rounded-3xl shadow-sm overflow-hidden" data-testid={`users-panel-${active.key}`}>
        {group.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-forest/50" data-testid={`users-empty-${active.key}`}>
            {q ? `No ${active.label.toLowerCase()} match "${userSearch}".` : `No ${active.label.toLowerCase()} yet.`}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-softstone/70 text-left text-forest/60 text-xs uppercase tracking-wide">
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Step</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Billing</th>
                  <th className="px-5 py-3">Promo Code</th>
                  <th className="px-5 py-3">Joined</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {group.map((u) => (
                  <tr key={u.user_id} className="border-t border-sage/10" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => openUserDetail(u)}
                        className="font-semibold text-forest hover:text-terracotta-dark hover:underline text-left"
                        data-testid={`user-name-btn-${u.user_id}`}
                      >
                        {u.name}
                      </button>
                      {u.bundle_member && (
                        <span className="ml-2 inline-block whitespace-nowrap text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 align-middle" data-testid={`user-bundle-badge-${u.user_id}`}>
                          Bundle Deal
                        </span>
                      )}
                      {u.free_access && (
                        <span className="ml-2 inline-block whitespace-nowrap text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-sage-light text-sage-dark align-middle" data-testid={`user-free-badge-${u.user_id}`}>
                          Free access
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-forest/70">{u.email}</td>
                    <td className="px-5 py-3">
                      <StepBadge step={u.onboarding_step || "Unknown"} testid={`user-step-${u.user_id}`} />
                    </td>
                    <td className="px-5 py-3 text-xs text-forest/60">
                      {["vendor", "event_creator", "marketplace_user"].includes(u.role) ? (u.vendor_status || "—") : (u.email_verified ? "verified" : "unverified")}
                    </td>
                    <td className="px-5 py-3">
                      {u.subscription_status === "active" ? (
                        <Button size="sm" variant="ghost" className="rounded-full text-terracotta-dark text-xs" onClick={() => setSubscription(u.user_id, "deactivate")} data-testid={`deactivate-sub-${u.user_id}`}>
                          Active — revoke
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-full text-sage-dark border-sage/40 text-xs hover:bg-sage-light" onClick={() => setSubscription(u.user_id, "activate")} data-testid={`activate-sub-${u.user_id}`}>
                          Activate
                        </Button>
                      )}
                    </td>
                    <td className="px-5 py-3" data-testid={`user-promo-code-${u.user_id}`}>
                      {u.signup_promo_code ? (
                        <span className="inline-block whitespace-nowrap text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-terracotta-light text-terracotta-dark">{u.signup_promo_code}</span>
                      ) : <span className="text-xs text-forest/40">—</span>}
                    </td>
                    <td className="px-5 py-3 text-xs text-forest/50">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {(u.onboarding_step || "Unknown") === "Active" ? (
                          <button
                            onClick={() => openEmailDialog(u)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-sage-dark hover:bg-sage-light transition"
                            data-testid={`email-user-${u.user_id}`}
                            aria-label={`Email ${u.email}`}
                          >
                            <Mail className="w-3.5 h-3.5" /> Email user
                          </button>
                        ) : (
                          <button
                            onClick={() => resendStepEmail(u)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-sage-dark hover:bg-sage-light transition"
                            data-testid={`resend-email-${u.user_id}`}
                            aria-label={`Resend ${u.onboarding_step} email to ${u.email}`}
                          >
                            <Mail className="w-3.5 h-3.5" /> Resend email
                          </button>
                        )}
                        <button
                          onClick={() => sendReset(u)}
                          disabled={resetBusy === u.user_id}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-sage-dark hover:bg-sage-light transition disabled:opacity-50"
                          data-testid={`send-reset-${u.user_id}`}
                          aria-label={`Send password reset link to ${u.email}`}
                        >
                          <KeyRound className="w-3.5 h-3.5" /> {resetBusy === u.user_id ? "Sending…" : "Send Password Reset Link"}
                        </button>
                        <button
                          onClick={() => deleteUser(u)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-terracotta-dark hover:bg-terracotta-light transition"
                          data-testid={`delete-user-${u.user_id}`}
                          aria-label={`Delete ${u.email}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
